import React from 'react';
import { FastField } from 'formik';
import {
  Box,
  Card,
  Flex,
  FormHelperText,
  Heading,
  Text,
  Icon,
  IconButton,
  Link,
  Tooltip,
} from 'pounceV2';
import FormikEditorV3 from 'Components/fields/Editor/EditorV3';
import { Link as RRLink } from 'react-router-dom';
import urls from 'Source/urls';
import { WRITING_POLICIES_DOC_URL, WRITING_RULES_DOC_URL } from 'Source/constants';
import { DetectionType } from 'Generated/schema';
import { useTheme } from '@mui/material';
import EditDetectionFilterFieldsCardV3 from './EditDetectionFilterFieldsCardV3';

interface BaseDetectionFormEditorSectionProps {
  type: DetectionType;
  isUpdate?: boolean;
  isManaged: boolean;
  functionOpen?: boolean;
  isYAML?: boolean;
  canEdit?: boolean;
}

const BaseDetectionFormEditorSectionV3: React.FC<BaseDetectionFormEditorSectionProps> = ({
  type,
  isUpdate,
  isManaged,
  functionOpen: initialFunctionOpen = true,
  isYAML = false,
  canEdit = true,
}) => {
  const {
    palette: { tokens },
  } = useTheme();
  const [functionOpen, setFunctionOpen] = React.useState(initialFunctionOpen);
  const isPolicy = type === DetectionType.Policy;
  const isRule = type === DetectionType.Rule;

  return (
    <Box>
      {isUpdate && isRule ? (
        <>
          <Flex alignItems="center" wrap="nowrap" gap={10}>
            <Icon type="thunder" color={tokens.content.assets.neutral.base} size="medium" />
            <Heading variant="h5">When the following conditions are true:</Heading>
          </Flex>
          <Box
            ml={2}
            mb={5}
            mt={2}
            pl={11}
            borderLeftStyle="solid"
            borderLeftWidth="1px"
            borderColor={tokens.stroke.neutral[5]}
          >
            {canEdit ? (
              <EditDetectionFilterFieldsCardV3 p={3} />
            ) : (
              <EditDetectionFilterFieldsCardV3 p={3} disabled={true} />
            )}
          </Box>
          <Flex alignItems="center" wrap="nowrap" gap={10}>
            <Icon
              type="notifications-active"
              color={tokens.content.assets.neutral.base}
              size="medium"
            />
            <Heading variant="h5">Trigger an alert where:</Heading>
          </Flex>
        </>
      ) : null}
      <Card variant="inverseRaisedToBrand" p={6} ml={isUpdate && isRule ? 14 : 0}>
        <Flex justifyContent="space-between" align="center" gap={1.5}>
          <Flex align="center" gap={4}>
            <IconButton
              variant="ghost"
              active={functionOpen}
              icon={functionOpen ? 'caret-up' : 'caret-down'}
              onClick={() => setFunctionOpen(!functionOpen)}
              size="small"
              aria-label="Toggle Editor visibility"
            />
            <Text>{isPolicy ? 'Policy Function' : 'Rule Function'} </Text>
          </Flex>
          {isManaged ? (
            <Text>Read Only</Text>
          ) : (
            <Tooltip content="View documentation around writing detections in Panther.">
              <Link
                href={isPolicy ? WRITING_POLICIES_DOC_URL : WRITING_RULES_DOC_URL}
                size="small"
                external
              >
                <Flex align="center" gap={1.5}>
                  <Icon type="docs" size="medium" />
                  Learn About Detection Writing
                </Flex>
              </Link>
            </Tooltip>
          )}
        </Flex>
        {functionOpen && (
          <Box mt={5} data-testid="function-textarea">
            <FastField
              as={FormikEditorV3}
              placeholder={`# Enter the body of the ${type.toLowerCase()} here...`}
              name={isYAML ? 'simpleDetectionBody' : 'body'}
              language={isYAML ? 'yaml' : 'python'}
              required
              aria-describedby="editor-helper-text"
              readOnly={isManaged || !canEdit}
            />
          </Box>
        )}
      </Card>
      <FormHelperText id="editor-helper-text" mt={3} ml={isUpdate && isRule ? 14 : 0}>
        Need to define re-usable functions? Define them in a
        <Link ml={1} size="small" as={RRLink} to={urls.build.globalHelpers.list()}>
          global helper
        </Link>
      </FormHelperText>
    </Box>
  );
};

export default React.memo(BaseDetectionFormEditorSectionV3);
